import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import { EMAIL_MESSAGE_TOPICS } from "./utils/messageTopics";
import { useEmailQueueForm } from "./hooks/useEmailQueueForm";

const RECENT_COLUMNS = [
  "Topic",
  "Loan Id",
  "Context State",
  "Queued At (UTC)",
  "Status",
  "Actions",
];

function formatQueuedAt(isoTimestamp: string): string {
  return isoTimestamp.replace("T", " ").slice(0, 19) + " UTC";
}

export function EmailQueuePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const {
    selectedTopic,
    loanId,
    setLoanId,
    contextState,
    setContextState,
    sending,
    log,
    resendingIds,
    openTopic,
    closeDialog,
    handleSend,
    resend,
  } = useEmailQueueForm();

  const filteredTopics = useMemo(() => {
    if (!searchQuery) return EMAIL_MESSAGE_TOPICS;
    return EMAIL_MESSAGE_TOPICS.filter((topic) =>
      topic.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  const isValidLoanId = /^\d+$/.test(loanId.trim());

  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Email Queue
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Pick a message topic and a Loan Id to push a message onto the email queue, triggering that
        email without going through the full application flow.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <TextField
          fullWidth
          placeholder="Search message topics..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 3 }}>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Message Topic</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTopics.length > 0 ? (
                filteredTopics.map((topic) => (
                  <TableRow
                    key={topic}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openTopic(topic)}
                  >
                    <TableCell>{topic}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 500 }}>
                        Queue →
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No topics found matching "{searchQuery}"
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Recently Queued Messages
      </Typography>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                {RECENT_COLUMNS.map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600 }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {log.length > 0 ? (
                log.map((entry) => {
                  const isResending = resendingIds.has(entry.id);
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.topic}</TableCell>
                      <TableCell>{entry.loanId}</TableCell>
                      <TableCell>{entry.contextState ?? "—"}</TableCell>
                      <TableCell>{formatQueuedAt(entry.queuedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status === "sent" ? "Sent" : "Failed"}
                          size="small"
                          color={entry.status === "sent" ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Resend this message">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => resend(entry)}
                              disabled={isResending}
                            >
                              {isResending ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ReplayIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={RECENT_COLUMNS.length} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Nothing queued yet this session - pick a topic above to send one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={selectedTopic !== null}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedTopic}
            </Typography>
            <IconButton onClick={closeDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            autoFocus
            label="Loan Id"
            variant="outlined"
            size="small"
            value={loanId}
            onChange={(e) => setLoanId(e.target.value)}
            error={loanId.length > 0 && !isValidLoanId}
            helperText={loanId.length > 0 && !isValidLoanId ? "Loan Id must be a number" : " "}
            margin="dense"
          />
          <TextField
            fullWidth
            label="Context State"
            placeholder="Optional"
            variant="outlined"
            size="small"
            value={contextState}
            onChange={(e) => setContextState(e.target.value)}
            margin="dense"
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeDialog} color="inherit" disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!isValidLoanId || sending}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
